import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Håller ett textfält synkat mellan URL, lokal state och debouncern utan
 * hoppande eller försvinnande bokstäver.
 *
 * Reglerna (invarianta):
 *  1. Lokal state är källan medan användaren skriver — vi skriver ALDRIG över
 *     `value` som svar på vår egen debouncade URL-push.
 *  2. När URL:en ändras EXTERNT (back/forward, snabblänk, `flush("")`,
 *     `resetAll()`) synkas lokal state tyst.
 *  3. Debouncern avbryts vid unmount, vid `flush()` och när `urlValue` blir
 *     lika med `value` (t.ex. via extern reset).
 *
 * Användning:
 *   const { value, setValue, flush } = useDebouncedSearchParam({
 *     urlValue: search.q,
 *     push: (next) => navigate({ search: (s) => ({ ...s, q: next || undefined, page: 1 }) }),
 *     delay: 300,
 *   });
 *
 *   <input value={value} onChange={(e) => setValue(e.target.value)} />
 *   <form onSubmit={(e) => { e.preventDefault(); flush(); }} />
 */
export function useDebouncedSearchParam(opts: {
  urlValue: string;
  push: (next: string) => void;
  delay?: number;
  /** Normaliserar värdet innan jämförelse (t.ex. `.trim()`). Default: identitet. */
  normalize?: (raw: string) => string;
}) {
  const { urlValue, push, delay = 300, normalize = (v) => v } = opts;

  const [value, setValueState] = useState(urlValue);

  // Håll koll på vad VI senast pushade till URL:en, för att kunna skilja
  // "URL:en ändrades av oss (ignorera)" från "URL:en ändrades externt (synka)".
  const lastPushedRef = useRef(urlValue);
  // Stabil referens till senaste `push` så debouncereffekten inte behöver
  // deppa på funktionsidentiteten (som ofta ändras mellan renders).
  const pushRef = useRef(push);
  useEffect(() => {
    pushRef.current = push;
  }, [push]);

  const timerRef = useRef<number | null>(null);
  const clearTimer = () => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  // Extern URL-ändring → synka lokal state (och avbryt eventuell in-flight
  // debounce så den inte skriver tillbaka det gamla värdet).
  useEffect(() => {
    if (urlValue !== lastPushedRef.current) {
      lastPushedRef.current = urlValue;
      clearTimer();
      setValueState(urlValue);
    }
  }, [urlValue]);

  // Debouncad push när användaren skriver.
  useEffect(() => {
    const normalized = normalize(value);
    if (normalized === normalize(lastPushedRef.current)) return;
    clearTimer();
    timerRef.current = window.setTimeout(() => {
      lastPushedRef.current = normalized;
      pushRef.current(normalized);
    }, delay);
    return clearTimer;
    // `normalize` antas stabil; `push` läses via ref.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, delay]);

  // Rensa vid unmount.
  useEffect(() => () => clearTimer(), []);

  const setValue = useCallback((next: string) => {
    setValueState(next);
  }, []);

  /** Skriv omedelbart (Enter/blur/submit). Använder aktuellt `value` om inget skickas. */
  const flush = useCallback(
    (override?: string) => {
      clearTimer();
      const next = normalize(override ?? value);
      if (next !== normalize(lastPushedRef.current)) {
        lastPushedRef.current = next;
        pushRef.current(next);
      }
      if (override !== undefined) setValueState(override);
    },
    [value, normalize],
  );

  return { value, setValue, flush };
}
